import {
  BIG_ONE,
  BIG_ZERO,
  gcdBig,
  parseBigIntToken,
} from "@/lib/tool-kit/math/bigint";

/**
 * Modular inverses by the extended Euclidean algorithm, and the Chinese remainder theorem.
 *
 * The table is the one taught in class: start from the rows (n, 1, 0) and (a, 0, 1) and
 * subtract quotient multiples until the remainder is 0. Every row keeps the invariant
 * r = n·s + a·t, so the row before the zero is the Bézout identity for the gcd, and when
 * the gcd is 1 the t column is the inverse of a.
 */

export const MAX_DIGITS = 60;
export const MAX_CONGRUENCES = 10;

export interface EuclidRow {
  index: number;
  /** Null for the two starting rows. */
  quotient: bigint | null;
  remainder: bigint;
  s: bigint;
  t: bigint;
}

export function mod(value: bigint, modulus: bigint): bigint {
  return ((value % modulus) + modulus) % modulus;
}

/** The extended Euclid table for (n, a), including the final row with remainder 0. */
export function euclidTable(n: bigint, a: bigint): EuclidRow[] {
  let previous: EuclidRow = {
    index: 0,
    quotient: null,
    remainder: n,
    s: BIG_ONE,
    t: BIG_ZERO,
  };
  let current: EuclidRow = {
    index: 1,
    quotient: null,
    remainder: a,
    s: BIG_ZERO,
    t: BIG_ONE,
  };
  const rows = [previous, current];
  while (current.remainder !== BIG_ZERO) {
    const quotient = previous.remainder / current.remainder;
    const next: EuclidRow = {
      index: rows.length,
      quotient,
      remainder: previous.remainder - quotient * current.remainder,
      s: previous.s - quotient * current.s,
      t: previous.t - quotient * current.t,
    };
    rows.push(next);
    previous = current;
    current = next;
  }
  return rows;
}

export interface InverseResult {
  a: bigint;
  /** a mod n, in 0 … n − 1. */
  reduced: bigint;
  n: bigint;
  gcd: bigint;
  /** The inverse in 0 … n − 1, or null when gcd(a, n) ≠ 1. */
  inverse: bigint | null;
  rows: EuclidRow[];
  /** The Bézout coefficients: n·s + reduced·t = gcd. */
  s: bigint;
  t: bigint;
}

/** The inverse of a modulo n (n ≥ 2), with the full working. */
export function modularInverse(a: bigint, n: bigint): InverseResult {
  const reduced = mod(a, n);
  const rows = euclidTable(n, reduced);
  // The row before the one that reaches remainder 0 holds the gcd.
  const gcdRow = rows.at(-2) ?? rows[0];
  const gcd = gcdRow.remainder;
  return {
    a,
    reduced,
    n,
    gcd,
    inverse: gcd === BIG_ONE ? mod(gcdRow.t, n) : null,
    rows,
    s: gcdRow.s,
    t: gcdRow.t,
  };
}

/* ----------------------------------------------------------- reading */

const SEPARATORS = /[\s_  ]+/g;
const SIGN = /^[+\-−]/;

export type IntegerRead =
  | { kind: "ok"; value: bigint }
  | { kind: "empty" | "invalid" | "too-long" };

export function readInteger(raw: string): IntegerRead {
  const text = raw.replace(SEPARATORS, "");
  if (text === "") {
    return { kind: "empty" };
  }
  const value = parseBigIntToken(text);
  if (value === null) {
    return { kind: "invalid" };
  }
  return text.replace(SIGN, "").length > MAX_DIGITS
    ? { kind: "too-long" }
    : { kind: "ok", value };
}

/* --------------------------------------------------------------- CRT */

export interface Congruence {
  remainder: bigint;
  modulus: bigint;
}

export interface CrtMerge {
  left: Congruence;
  right: Congruence;
  gcd: bigint;
  /** right.remainder − left.remainder. */
  difference: bigint;
  /** The merged congruence, or null when the two disagree. */
  merged: Congruence | null;
  /** x = left.remainder + left.modulus × k. */
  k: bigint | null;
}

export interface ClassicRow {
  congruence: Congruence;
  /** M ÷ mᵢ. */
  partial: bigint;
  /** The inverse of M ÷ mᵢ modulo mᵢ. */
  inverse: bigint;
  /** rᵢ × (M ÷ mᵢ) × yᵢ. */
  term: bigint;
}

export interface ClassicMethod {
  product: bigint;
  rows: ClassicRow[];
  sum: bigint;
}

export interface CrtResult {
  congruences: Congruence[];
  merges: CrtMerge[];
  /** The combined congruence x ≡ r (mod lcm), or null when the system has no solution. */
  solution: Congruence | null;
  pairwiseCoprime: boolean;
  /** The textbook M, Mᵢ, yᵢ table, only when the moduli are pairwise coprime. */
  classic: ClassicMethod | null;
}

/** Combine two congruences into one modulo their lcm, or report that they conflict. */
export function mergeCongruences(
  left: Congruence,
  right: Congruence
): CrtMerge {
  const gcd = gcdBig(left.modulus, right.modulus);
  const difference = right.remainder - left.remainder;
  if (difference % gcd !== BIG_ZERO) {
    return { left, right, gcd, difference, merged: null, k: null };
  }
  const reducedLeft = left.modulus / gcd;
  const reducedRight = right.modulus / gcd;
  // reducedLeft × k ≡ difference ÷ gcd (mod reducedRight)
  const k =
    reducedRight === BIG_ONE
      ? BIG_ZERO
      : mod(
          (difference / gcd) *
            (modularInverse(reducedLeft, reducedRight).inverse ?? BIG_ZERO),
          reducedRight
        );
  const modulus = left.modulus * reducedRight;
  return {
    left,
    right,
    gcd,
    difference,
    merged: {
      remainder: mod(left.remainder + left.modulus * k, modulus),
      modulus,
    },
    k,
  };
}

function classicMethod(congruences: readonly Congruence[]): ClassicMethod {
  const product = congruences.reduce((p, c) => p * c.modulus, BIG_ONE);
  const rows = congruences.map((congruence) => {
    const partial = product / congruence.modulus;
    const inverse =
      modularInverse(partial, congruence.modulus).inverse ?? BIG_ZERO;
    return {
      congruence,
      partial,
      inverse,
      term: congruence.remainder * partial * inverse,
    };
  });
  return {
    product,
    rows,
    sum: rows.reduce((total, row) => total + row.term, BIG_ZERO),
  };
}

function pairwiseCoprime(congruences: readonly Congruence[]): boolean {
  for (let i = 0; i < congruences.length; i += 1) {
    for (let j = i + 1; j < congruences.length; j += 1) {
      if (gcdBig(congruences[i].modulus, congruences[j].modulus) !== BIG_ONE) {
        return false;
      }
    }
  }
  return true;
}

/** Solve x ≡ rᵢ (mod mᵢ) for moduli that need not be coprime. Moduli must be ≥ 2. */
export function solveCrt(input: readonly Congruence[]): CrtResult {
  const congruences = input.map((c) => ({
    remainder: mod(c.remainder, c.modulus),
    modulus: c.modulus,
  }));
  const coprime = pairwiseCoprime(congruences);
  const merges: CrtMerge[] = [];
  let current: Congruence | null = congruences[0] ?? null;
  for (const next of congruences.slice(1)) {
    if (current === null) {
      break;
    }
    const merge = mergeCongruences(current, next);
    merges.push(merge);
    current = merge.merged;
  }
  return {
    congruences,
    merges,
    solution: current,
    pairwiseCoprime: coprime,
    classic:
      coprime && congruences.length > 1 ? classicMethod(congruences) : null,
  };
}

/* ----------------------------------------------------- reading a system */

/** "x ≡ 2 (mod 3)", "2 mod 3", "2 % 3", "2, 3" and "2 3" all mean r = 2, m = 3. */
const SYSTEM_PREFIX = /^\s*[a-z]\s*(?:≡|==?|≡)/i;
const SYSTEM_NOISE = /[()]|\bmod\b|%/gi;
const SYSTEM_SPLIT = /[\s,;]+/;

export interface SystemLine {
  line: number;
  text: string;
}

export interface SystemRead {
  congruences: Congruence[];
  /** Lines that are not two whole numbers. */
  invalid: SystemLine[];
  /** Lines whose modulus is below 2. */
  smallModulus: SystemLine[];
  tooMany: boolean;
  tooLong: boolean;
}

export function readSystem(raw: string): SystemRead {
  const result: SystemRead = {
    congruences: [],
    invalid: [],
    smallModulus: [],
    tooMany: false,
    tooLong: false,
  };
  const lines = raw.split("\n");
  for (const [index, text] of lines.entries()) {
    const cleaned = text
      .replace(SYSTEM_PREFIX, " ")
      .replace(SYSTEM_NOISE, " ")
      .trim();
    if (cleaned === "") {
      continue;
    }
    const tokens = cleaned.split(SYSTEM_SPLIT);
    const numbers = tokens.map(readInteger);
    const entry = { line: index + 1, text: text.trim() };
    if (
      tokens.length !== 2 ||
      numbers.some((n) => n.kind === "invalid" || n.kind === "empty")
    ) {
      result.invalid.push(entry);
      continue;
    }
    if (numbers.some((n) => n.kind === "too-long")) {
      result.tooLong = true;
      continue;
    }
    const [remainder, modulus] = numbers.map((n) =>
      n.kind === "ok" ? n.value : BIG_ZERO
    );
    if (modulus < BigInt(2)) {
      result.smallModulus.push(entry);
      continue;
    }
    result.congruences.push({ remainder, modulus });
  }
  result.tooMany = result.congruences.length > MAX_CONGRUENCES;
  return result;
}
